import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Reply, Send, X } from "lucide-react";
import { useSettings } from "../../contexts/SettingsContext";

export default function AdminContactMessages() {
    const { settings } = useSettings();
    const [items, setItems] = React.useState([]);
    const [replyTo, setReplyTo] = React.useState(null);
    const [draft, setDraft] = React.useState({ subject: "", body: "" });
    const [sending, setSending] = React.useState(false);

    const load = () => api.get("/admin/contact-messages").then(r => setItems(r.data || []));
    React.useEffect(() => { load(); }, []);

    const openReply = (m) => {
        const company = settings?.site_name || settings?.company_name || "ccashop";
        setReplyTo(m);
        setDraft({
            subject: `Re: ${m.subject || "Mesajul tău"}`,
            body: `Salut ${m.name || ""},\n\n\n\n—\nEchipa ${company}`,
        });
    };

    const send = async () => {
        if (!draft.body.trim()) return toast.error("Scrie un mesaj");
        setSending(true);
        try {
            await api.post(`/admin/contact-messages/${replyTo.contact_id}/reply`, draft);
            toast.success("Email trimis");
            setReplyTo(null); setDraft({ subject: "", body: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la trimitere");
        }
        setSending(false);
    };

    return (
        <div data-testid="admin-messages">
            <div className="overline mb-2">Administrare</div>
            <h1 className="font-heading text-3xl uppercase mb-6">Mesaje contact</h1>
            <div className="space-y-3">
                {items.length === 0 && <p className="font-mono text-muted-foreground">Nu există mesaje.</p>}
                {items.map((m, i) => (
                    <div key={m.contact_id} className="border-2 border-foreground p-4" data-testid={`message-${i}`}>
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <div className="font-heading">{m.name}</div>
                                <div className="text-xs font-mono text-muted-foreground">{m.email} · {m.phone}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="text-xs font-mono text-muted-foreground">{new Date(m.created_at).toLocaleString("ro-RO")}</div>
                                {m.email && (
                                    <button onClick={() => openReply(m)} className="btn-outline text-xs flex items-center gap-1" data-testid={`reply-btn-${i}`}>
                                        <Reply className="h-3 w-3" /> Răspunde
                                    </button>
                                )}
                            </div>
                        </div>
                        {m.subject && <div className="font-mono uppercase text-xs tracking-widest mb-1">{m.subject}</div>}
                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                        {(m.replies || []).length > 0 && (
                            <div className="mt-3 pl-4 border-l-2 border-primary/30 space-y-2">
                                {m.replies.map((r) => (
                                    <div key={r.reply_id} className="text-xs">
                                        <div className="text-muted-foreground">
                                            <span className="text-primary">↳ Răspuns de {r.by_user_name}</span> · {new Date(r.at).toLocaleString("ro-RO")}
                                            {r.sent ? " · ✓ trimis" : " · ⚠ ne-trimis"}
                                        </div>
                                        <div className="text-sm whitespace-pre-wrap mt-1">{r.body}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {replyTo && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-xl w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="reply-modal">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="font-heading text-xl">Răspunde la {replyTo.name}</h2>
                                <div className="text-xs text-muted-foreground">Către: {replyTo.email}</div>
                            </div>
                            <button onClick={() => setReplyTo(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <input value={draft.subject} onChange={(e) => setDraft(d => ({ ...d, subject: e.target.value }))}
                               placeholder="Subiect" className="input-soft" data-testid="reply-subject" />
                        <textarea value={draft.body} onChange={(e) => setDraft(d => ({ ...d, body: e.target.value }))}
                                  rows="10" placeholder="Mesajul tău..." className="input-soft" data-testid="reply-body" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setReplyTo(null)} className="btn-outline">Anulează</button>
                            <button onClick={send} disabled={sending} className="btn-primary flex items-center gap-2" data-testid="reply-send">
                                <Send className="h-4 w-4" /> {sending ? "Se trimite..." : "Trimite email"}
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground">Necesită SMTP configurat. Dacă SMTP nu e setat, răspunsul va fi salvat ca ciornă.</p>
                    </div>
                </div>
            )}
        </div>
    );
}
